import { useState, useCallback } from 'react';

import { getDateFormat, getFormattedDate, showToast } from '../../../helper';
import API from '../../../helper/API';
import { DATE_FORMAT_BE, OA_DATE_FORMAT_BE } from '../../../constants';
import { IHotelProvider, IHotelQueryParams, IPageInfo } from '../type';
import FileDownload from 'js-file-download';
import { flightFilterNavs } from '../../order-management/constants';
import HotelContext, { IHotelContext } from './HotelContext';

const HotelProvider = (props: IHotelProvider) => {
  const { queryParams } = props?.data;
  const [statusChange, setStatusChange] = useState<boolean>(false);
  const [report, setReport] = useState<{ isLoading: boolean }>({ isLoading: false });
  const [pageInfo, setPageInfo] = useState<IPageInfo>({
    totalCount: 0,
    totalPages: 0,
    count: 0,
    size: 10,
    index: 1
  });

  const handleApiError = useCallback((error: any, message?: string) => {
    const errorMessage = error?.message || message || 'An error occurred';
    showToast(errorMessage, 'error');
    throw new Error(`API error: ${errorMessage}`);
  }, []);

  const getHotelOrderList = useCallback(async (cb?: (response?: any) => void) => {
    try {
      const response = await API.get(`/hotel/order/dashboard?${buildQueryParams(queryParams)}`);
      const resultData = response?.data?.data;
      setPageInfo(prev => ({
        ...prev,
        totalCount: resultData?.totalElements,
        totalPages: resultData?.totalPages,
        count: resultData?.numberOfElements
      }));
      cb?.(resultData);
    } catch (error: any) {
      handleApiError(error);
      cb?.(error);
    }
  }, [queryParams, handleApiError]);

  const getOrderById = useCallback(async (id: string, cb?: (response?: any) => void) => {
    try {
      const response = await API.get(`/hotel/order/${id}`);
      const resultData = response?.data?.data;
      cb?.(resultData);
    } catch (error: any) {
      handleApiError(error);
      cb?.(error);
    }
  }, [handleApiError]);

  const getCancelReasons = useCallback(async (id: string, cb?: (response?: any) => void) => {
    try {
      const response = await API.get(`/hotel/order/cancel/info/${id}`);
      const resultData = response?.data?.data;
      cb?.(resultData);
    } catch (error: any) {
      handleApiError(error);
      cb?.(error);
    }
  }, [handleApiError]);

  const cancelFlightsBooking = useCallback(async (
    id: string,
    requestbody: any,
    cb?: (response?: any) => void
  ) => {
    setStatusChange(false);
    try {
      const response = await API.post(`/hotel/order/cancel/${id}`, requestbody);
      setStatusChange(true);
      const resultData = response?.data?.data;
      showToast('Flight cancelled successfully', 'success');
      cb?.(resultData);
    } catch (error: any) {
      setStatusChange(false);
      handleApiError(error);
      cb?.(error);
    }
  }, [handleApiError]);

  const updateStatus = useCallback(async (draft: any, cb?: (response?: any) => void) => {
    setStatusChange(false);
    try {
      let queryParams = `status=${draft?.status}`;
      if (draft?.status === 'BOOKING_CONFIRMED' && draft?.bookingId) {
        queryParams = `${queryParams}&bookingId=${draft?.bookingId}`;
      }
      const response = await API.put(`/hotel/order/update/${draft?.id}?${queryParams}`);
      setStatusChange(true);
      const resultData = response?.data?.data;
      showToast('Status updated successfully', 'success');
      cb?.(resultData);
    } catch (error: any) {
      setStatusChange(false);
      handleApiError(error);
      cb?.(error);
    }
  }, [handleApiError]);

  const downloadReport = useCallback(async () => {
    try {
      setReport(prev => ({ ...prev, isLoading: true }));
      const APITemp: any = API;
      APITemp.responseType = 'blob';
      const response = await APITemp.get(`/hotel/order/report?${buildQueryParamsReport(queryParams)}`);
      showToast('Report generated!', 'success');
      FileDownload(response?.data, 'report.csv');
    } catch (error: any) {
      handleApiError(error);
    } finally {
      setReport(prev => ({ ...prev, isLoading: false }));
    }
  }, [queryParams, handleApiError]);

  const downloadInvoice = useCallback(async (orderID: string, cb?: (response?: any) => void) => {
    try {
      const response = await API.get(`/hotel/order/invoice/${orderID}`);
      const resultData = response?.data?.data;
      showToast('Invoice downloaded successfully', 'success');
      cb?.(resultData);
    } catch (error: any) {
      handleApiError(error);
      cb?.(error);
    }
  }, [handleApiError]);

  const downloadVoucher = useCallback(async (orderID: string, cb?: (response?: any) => void) => {
    try {
      const response = await API.get(`/hotel/order/voucher/${orderID}`);
      const resultData = response?.data?.data;
      showToast('Voucher downloaded successfully', 'success');
      cb?.(resultData);
    } catch (error: any) {
      handleApiError(error);
      cb?.(error);
    }
  }, [handleApiError]);

  const contextValue: IHotelContext = {
    getHotelOrderList,
    getOrderById,
    getCancelReasons,
    cancelFlightsBooking,
    updateStatus,
    downloadReport,
    downloadInvoice,
    downloadVoucher,
    report,
    pageInfo,
    statusChange
  };

  return <HotelContext.Provider value={contextValue}>{props?.children}</HotelContext.Provider>;
};

export default HotelProvider;

const buildQueryParams = (queryParams: IHotelQueryParams): string => {
  let queryStr = `page=${queryParams?.pageIndex - 1}&size=10`;
  
  if (queryParams?.search) {
    queryStr = `${queryStr}&search=${queryParams.search}`;
  }
  
  if (queryParams?.dateRange?.length > 0) {
    const startDate = queryParams.dateRange[0]
      ? getFormattedDate(queryParams.dateRange[0], OA_DATE_FORMAT_BE)
      : null;
    const endDate = queryParams.dateRange[1]
      ? getFormattedDate(queryParams.dateRange[1], OA_DATE_FORMAT_BE)
      : null;
    queryStr = `${queryStr}&start=${startDate}&end=${endDate}`;
  }

  if (queryParams?.sortBy && queryParams?.sortByField) {
    queryStr = `${queryStr}&sortByField=${queryParams.sortByField}&sortBy=${queryParams.sortBy}`;
  }

  if (queryParams?.orderStatus) {
    const isFilterNav = flightFilterNavs?.find((obj: any) => obj === queryParams.orderStatus);
    if (isFilterNav && queryParams.orderStatus !== 'ORDER_DRAFT') {
      queryStr = `${queryStr}&status=${queryParams.orderStatus}`;
    }
  }

  return queryStr;
};

const buildQueryParamsReport = (queryParams: IHotelQueryParams): string => {
  const startDate = getDateFormat(queryParams?.dateRange?.[0] as Date, DATE_FORMAT_BE);
  const endDate = getDateFormat(queryParams?.dateRange?.[1] as Date, DATE_FORMAT_BE);
  return `start=${startDate}&end=${endDate}`;
};
